import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import {
    Box, Typography, Button, Grid, Card, Chip, Alert, Dialog, DialogContent, DialogActions,
    IconButton, Tooltip, FormControlLabel, Checkbox,
} from '@mui/material';
import EnhancedEncryptionRoundedIcon from '@mui/icons-material/EnhancedEncryptionRounded';
import GppGoodRoundedIcon from '@mui/icons-material/GppGoodRounded';
import VpnKeyRoundedIcon from '@mui/icons-material/VpnKeyRounded';
import AutorenewRoundedIcon from '@mui/icons-material/AutorenewRounded';
import LockResetRoundedIcon from '@mui/icons-material/LockResetRounded';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import http, { apiErrorMessage } from '../Api/http';
import {
    GetSalarySecurityStatus, PostSalarySecuritySetup, PostSalaryChangePassphrase,
    PostSalaryResetViaRecovery, PostSalaryRotateRecoveryKey,
} from '../Api/Api';
import SecretDialog, { DialogHeader } from '../components/SecretDialog';
import SnackBar from '../components/SnackBar';
import { solidBtn, SkeletonCards } from '../components/uiKit';
import { Shimmer } from '../components/ContentLoader';
import { selectModules } from '../redux/slices/authSlice';
import { hasModule } from '../data/serverModules';
import { passphraseConfig, clearSalaryPassphrase, passphraseErrorText } from '../utils/salaryPassphrase';
import { PRIMARY, PRIMARY_LIGHT, PRIMARY_DARK, PRIMARY_BORDER } from '../theme';

const explain = (err, fallback) => passphraseErrorText(err) || apiErrorMessage(err, fallback);

const HOW_IT_WORKS = [
    'Salary figures are encrypted at rest. They are unreadable without the Encryption Password.',
    'The password is shared by the whole entity. Anyone whose role has "Unlock Salary Figures" and knows it can view real figures.',
    'The 24-character recovery key resets a forgotten password without losing any data. It is shown once, so store it safely.',
];

// One tile in the actions grid.
function ActionCard({ icon: Icon, color, bg, title, text, button, onClick, primary, disabled }) {
    return (
        <Card sx={{ height: '100%', border: '1px solid #ECEBF5', borderRadius: '10px', boxShadow: '0 1px 3px rgba(16,24,40,0.05)', p: 2.5, display: 'flex', flexDirection: 'column', gap: 1.4 }}>
            <Box sx={{ width: 46, height: 46, borderRadius: '12px', bgcolor: bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Icon sx={{ fontSize: 24, color }} />
            </Box>
            <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontSize: 15, fontWeight: 800, color: '#111827' }}>{title}</Typography>
                <Typography sx={{ fontSize: 12.5, color: '#6B7280', mt: 0.4, lineHeight: 1.5 }}>{text}</Typography>
            </Box>
            <Button
                onClick={onClick} fullWidth disabled={disabled}
                sx={primary ? { ...solidBtn, height: 40, '&.Mui-disabled': { bgcolor: '#E5E7EB', color: '#9CA3AF' } } : { textTransform: 'none', fontWeight: 700, fontSize: 13, height: 40, borderRadius: '7px', color: PRIMARY_DARK, bgcolor: PRIMARY_LIGHT, border: `1px solid ${PRIMARY_BORDER}`, '&:hover': { bgcolor: '#E8E3FD' }, '&.Mui-disabled': { bgcolor: '#F3F4F6', color: '#9CA3AF', borderColor: '#E5E7EB' } }}
            >
                {button}
            </Button>
        </Card>
    );
}

export default function SalarySecurityPage() {
    const modules = useSelector(selectModules);
    const canManage = hasModule(modules, 'salary-unlock');

    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadFailed, setLoadFailed] = useState(false);
    const [panel, setPanel] = useState(null); // 'setup' | 'change' | 'forgot' | 'rotate' | 'recovery' | null
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [recoveryKey, setRecoveryKey] = useState('');
    const [recoverySaved, setRecoverySaved] = useState(false);
    const [snack, setSnack] = useState({ open: false, ok: false, msg: '' });
    const showSnack = (msg, ok) => setSnack({ open: true, ok, msg });

    const loadStatus = useCallback(async () => {
        if (!canManage) { setLoading(false); return null; }
        setLoadFailed(false);
        try {
            const res = await http.get(GetSalarySecurityStatus);
            const d = res.data?.data || null;
            setStatus(d);
            return d;
        } catch { setStatus(null); setLoadFailed(true); return null; } finally { setLoading(false); }
    }, [canManage]);

    useEffect(() => {
        loadStatus();
        // This page never keeps the passphrase: whatever a salary screen held is dropped here too.
        clearSalaryPassphrase();
        return () => clearSalaryPassphrase();
    }, [loadStatus]);

    const setupDone = status?.setupCompleted === true;
    // When the status could not be loaded the real state is unknown: keep the manage cards usable, the server validates each action. Setup stays disabled: it is a one-time action and may already be done.
    const unknown = loadFailed;
    const lockedUntil = status?.pinLockedUntil && new Date(status.pinLockedUntil) > new Date() ? new Date(status.pinLockedUntil) : null;
    const recoveryLockedUntil = status?.recoveryLockedUntil && new Date(status.recoveryLockedUntil) > new Date() ? new Date(status.recoveryLockedUntil) : null;

    const open = (name) => { setError(''); setPanel(name); };
    const close = () => { if (!busy) setPanel(null); };

    const checkNew = (pass, confirm) => {
        if (pass.length < 8) return 'Use at least 8 characters.';
        if (pass !== confirm) return 'The two passwords do not match.';
        return '';
    };

    const submitSetup = async ({ pass, confirm }) => {
        const bad = checkNew(pass, confirm);
        if (bad) { setError(bad); return; }
        setBusy(true); setError('');
        try {
            const res = await http.post(PostSalarySecuritySetup, null, passphraseConfig(pass));
            setRecoverySaved(false);
            setRecoveryKey(res.data?.data?.recoveryKey || '');
            setPanel('recovery');
        } catch (err) { setError(explain(err, 'Setup failed.')); } finally { setBusy(false); }
    };

    const submitChange = async ({ current, pass, confirm }) => {
        const bad = checkNew(pass, confirm);
        if (bad) { setError(bad); return; }
        setBusy(true); setError('');
        try {
            await http.post(PostSalaryChangePassphrase, { newPassphrase: pass }, passphraseConfig(current));
            setPanel(null);
            showSnack('Password changed.', true);
        } catch (err) { setError(explain(err, 'Could not change the password.')); loadStatus(); } finally { setBusy(false); }
    };

    const submitForgot = async ({ key, pass, confirm }) => {
        const bad = checkNew(pass, confirm);
        if (bad) { setError(bad); return; }
        setBusy(true); setError('');
        try {
            await http.post(PostSalaryResetViaRecovery, { newPassphrase: pass }, { headers: { 'X-Recovery-Key': key.trim() } });
            setPanel(null);
            showSnack('Password reset. Your recovery key still works.', true);
        } catch (err) {
            setError(err?.response?.status === 401 ? 'Incorrect recovery key.' : explain(err, 'Could not reset the password.'));
            loadStatus();
        } finally { setBusy(false); }
    };

    const submitRotate = async ({ current }) => {
        setBusy(true); setError('');
        try {
            const res = await http.post(PostSalaryRotateRecoveryKey, null, passphraseConfig(current));
            setRecoverySaved(false);
            setRecoveryKey(res.data?.data?.recoveryKey || '');
            setPanel('recovery');
        } catch (err) { setError(explain(err, 'Could not rotate the recovery key.')); loadStatus(); } finally { setBusy(false); }
    };

    const copyKey = async () => {
        try { await navigator.clipboard.writeText(recoveryKey); showSnack('Recovery key copied.', true); }
        catch { showSnack('Copy failed — select the key and copy it by hand.', false); }
    };

    // The key is shown once. Done flips the page to "Protected" without another request.
    const closeRecovery = () => {
        setRecoveryKey('');
        setPanel(null);
        setStatus((st) => ({ ...(st || {}), setupCompleted: true }));
    };

    return (
        <>
            <SnackBar open={snack.open} color={snack.ok} setOpen={(o) => setSnack((s) => ({ ...s, open: o }))} status={snack.ok} message={snack.msg} />

            <Box sx={{ px: { xs: 1.5, md: 2.5 }, pt: 2.5, pb: 3, minHeight: '88vh' }}>
                {!canManage && (
                    <Alert severity="warning" sx={{ borderRadius: '7px', fontSize: 12.5 }}>
                        Your role does not have the "Unlock Salary Figures" permission. Ask an administrator to enable it under Roles &amp; Access.
                    </Alert>
                )}

                {canManage && loadFailed && (
                    <Alert
                        severity="warning" sx={{ mb: 2, borderRadius: '7px', fontSize: 12.5 }}
                        action={<Button size="small" onClick={() => { setLoading(true); loadStatus(); }} sx={{ textTransform: 'none', fontWeight: 700 }}>Retry</Button>}
                    >
                        Could not load the salary security status. The server may not have this feature deployed yet.
                    </Alert>
                )}

                {canManage && !loading && !lockedUntil && Number(status?.pinAttemptsRemaining) <= 2 && Number(status?.failedPinAttempts) > 0 && (
                    <Alert severity="warning" sx={{ mb: 2, borderRadius: '7px', fontSize: 12.5 }}>
                        {status.pinAttemptsRemaining} password attempt{Number(status.pinAttemptsRemaining) === 1 ? '' : 's'} left before lockout ({status.failedPinAttempts} of {status.pinFailureThreshold} used).
                    </Alert>
                )}
                {canManage && !loading && !recoveryLockedUntil && Number(status?.recoveryAttemptsRemaining) <= 2 && Number(status?.failedRecoveryAttempts) > 0 && (
                    <Alert severity="warning" sx={{ mb: 2, borderRadius: '7px', fontSize: 12.5 }}>
                        {status.recoveryAttemptsRemaining} recovery key attempt{Number(status.recoveryAttemptsRemaining) === 1 ? '' : 's'} left before lockout ({status.failedRecoveryAttempts} of {status.recoveryFailureThreshold} used).
                    </Alert>
                )}

                {canManage && (
                    <>
                        <Card sx={{
                            border: '1px solid #E7E1FB', borderRadius: '12px', boxShadow: '0 1px 3px rgba(124,92,252,0.06)', mb: 2.5, p: { xs: 2.2, md: 3 },
                            background: 'linear-gradient(135deg, #F5F2FE 0%, #FBFAFF 55%, #F1EEFE 100%)',
                            display: 'flex', alignItems: 'center', gap: 2.2, flexWrap: 'wrap',
                        }}>
                            <Box sx={{ width: 64, height: 64, borderRadius: '18px', bgcolor: '#fff', border: '1px solid #E7E1FB', boxShadow: '0 2px 8px rgba(124,92,252,0.18)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                {loading
                                    ? <Shimmer w={34} h={34} r={9} />
                                    : setupDone ? <GppGoodRoundedIcon sx={{ fontSize: 36, color: '#16A34A' }} /> : <EnhancedEncryptionRoundedIcon sx={{ fontSize: 36, color: PRIMARY }} />}
                            </Box>
                            {loading ? (
                                <Box sx={{ flex: 1, minWidth: 220, display: 'flex', flexDirection: 'column', gap: 1.2 }}>
                                    <Shimmer w={200} h={20} />
                                    <Shimmer w="70%" h={12} />
                                </Box>
                            ) : (
                            <Box sx={{ flex: 1, minWidth: 220 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                    <Typography sx={{ fontSize: 20, fontWeight: 800, color: '#111827' }}>Salary Security</Typography>
                                    {!loading && !loadFailed && (
                                        <Chip
                                            size="small"
                                            icon={setupDone ? <CheckCircleRoundedIcon sx={{ fontSize: '15px !important' }} /> : undefined}
                                            label={setupDone ? 'Protected' : 'Not set up'}
                                            sx={{ fontWeight: 700, fontSize: 11.5, ...(setupDone ? { bgcolor: '#DCFCE7', color: '#16A34A', '& .MuiChip-icon': { color: '#16A34A' } } : { bgcolor: '#FEF3C7', color: '#B45309' }) }}
                                        />
                                    )}
                                </Box>
                                <Typography sx={{ fontSize: 13, color: '#6B7280', mt: 0.5, maxWidth: 640 }}>
                                    {setupDone
                                        ? 'Salary figures for this entity are encrypted. Manage the Encryption Password and the recovery key here.'
                                        : 'Protect every salary figure with an Encryption Password. Set it up once for this entity.'}
                                </Typography>
                                {(lockedUntil || recoveryLockedUntil) && (
                                    <Typography sx={{ fontSize: 12, color: '#B91C1C', fontWeight: 600, mt: 0.8 }}>
                                        {lockedUntil ? `Password attempts locked until ${lockedUntil.toLocaleString()}. ` : ''}
                                        {recoveryLockedUntil ? `Recovery key attempts locked until ${recoveryLockedUntil.toLocaleString()}.` : ''}
                                    </Typography>
                                )}
                            </Box>
                            )}
                        </Card>

                        {loading && <SkeletonCards count={4} height={190} size={{ xs: 12, sm: 6, md: 3 }} p={0} />}

                        {!loading && (
                            <Grid container spacing={2} sx={{ mb: 2.5 }}>
                                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                    <ActionCard
                                        icon={setupDone ? GppGoodRoundedIcon : EnhancedEncryptionRoundedIcon}
                                        color={setupDone ? '#16A34A' : PRIMARY} bg={setupDone ? '#DCFCE7' : PRIMARY_LIGHT}
                                        title="Encryption setup"
                                        text={setupDone ? 'Salary encryption is switched on for this entity.' : 'Choose an encryption password. A recovery key is generated and shown once; every existing salary structure is encrypted.'}
                                        button={setupDone ? 'Enabled' : 'Set up encryption'} onClick={() => open('setup')} primary={!setupDone && !unknown} disabled={setupDone || unknown}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                    <ActionCard
                                        icon={VpnKeyRoundedIcon} color="#6246E0" bg="#EFECFE"
                                        title="Change password"
                                        text="Replace the encryption password. You need the current one. No salary data is touched."
                                        button="Change password" onClick={() => open('change')} primary={setupDone || unknown} disabled={!setupDone && !unknown}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                    <ActionCard
                                        icon={AutorenewRoundedIcon} color="#0EA5E9" bg="#E0F2FE"
                                        title="Generate new recovery key"
                                        text="Issue a brand-new recovery key. The old one stops working immediately."
                                        button="Generate new key" onClick={() => open('rotate')} disabled={!setupDone && !unknown}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                    <ActionCard
                                        icon={LockResetRoundedIcon} color="#F59E0B" bg="#FFF7ED"
                                        title="Forgot password"
                                        text="Reset the password with the 24-character recovery key. Nothing is re-encrypted or lost."
                                        button="Reset with recovery key" onClick={() => open('forgot')} disabled={!setupDone && !unknown}
                                    />
                                </Grid>
                            </Grid>
                        )}

                        <Card sx={{ border: '1px solid #ECEBF5', borderRadius: '10px', boxShadow: 'none', p: 2.5, mt: loading ? 2.5 : 0 }}>
                            {loading ? (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.4 }}>
                                    <Shimmer w={110} h={14} />
                                    {[92, 96, 84].map((w) => (
                                        <Box key={w} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Shimmer w={17} h={17} r={9} />
                                            <Shimmer w={`${w}%`} h={12} />
                                        </Box>
                                    ))}
                                </Box>
                            ) : (
                                <>
                                    <Typography sx={{ fontSize: 13, fontWeight: 800, color: '#111827', mb: 1 }}>How it works</Typography>
                                    {HOW_IT_WORKS.map((t) => (
                                        <Box key={t} sx={{ display: 'flex', gap: 1, py: 0.5 }}>
                                            <CheckCircleRoundedIcon sx={{ fontSize: 17, color: PRIMARY, mt: '2px', flexShrink: 0 }} />
                                            <Typography sx={{ fontSize: 12.5, color: '#475569', lineHeight: 1.55 }}>{t}</Typography>
                                        </Box>
                                    ))}
                                </>
                            )}
                        </Card>
                    </>
                )}
            </Box>

            <SecretDialog
                open={panel === 'setup'} icon={EnhancedEncryptionRoundedIcon} title="Set up salary encryption"
                subtitle="Choose an Encryption Password. A recovery key is shown once after setup."
                fields={[{ key: 'pass', label: 'New password', helper: 'At least 8 characters' }, { key: 'confirm', label: 'Confirm password' }]}
                submitLabel="Set up" busy={busy} error={error} onClose={close} onSubmit={submitSetup}
            />
            <SecretDialog
                open={panel === 'change'} icon={VpnKeyRoundedIcon} title="Change password"
                fields={[{ key: 'current', label: 'Current password' }, { key: 'pass', label: 'New password', helper: 'At least 8 characters' }, { key: 'confirm', label: 'Confirm new password' }]}
                submitLabel="Change" busy={busy} error={error} onClose={close} onSubmit={submitChange}
            />
            <SecretDialog
                open={panel === 'forgot'} icon={LockResetRoundedIcon} title="Forgot password" subtitle="Use the 24-character recovery key issued at setup. No salary data is lost."
                fields={[{ key: 'key', label: 'Recovery key' }, { key: 'pass', label: 'New password', helper: 'At least 8 characters' }, { key: 'confirm', label: 'Confirm new password' }]}
                submitLabel="Reset password" busy={busy} error={error} onClose={close} onSubmit={submitForgot}
            />
            <SecretDialog
                open={panel === 'rotate'} icon={AutorenewRoundedIcon} title="Generate new recovery key" subtitle="The old recovery key stops working immediately."
                fields={[{ key: 'current', label: 'Current password' }]} submitLabel="Generate" busy={busy} error={error}
                onClose={close} onSubmit={submitRotate}
            />

            <Dialog open={panel === 'recovery'} maxWidth="xs" fullWidth slotProps={{ paper: { sx: { borderRadius: '7px', overflow: 'hidden' } } }}>
                <DialogHeader icon={VpnKeyRoundedIcon} title="Save your recovery key" subtitle="It is shown only once and cannot be retrieved later." />
                <DialogContent sx={{ px: 2.5, pt: '20px !important', pb: 1.5, display: 'flex', flexDirection: 'column', gap: 1.6 }}>
                    <Alert severity="warning" sx={{ fontSize: 12, borderRadius: '7px' }}>
                        Without this key a forgotten password cannot be reset. Store it somewhere safe, not on this computer.
                    </Alert>
                    <Box sx={{ p: 1.6, borderRadius: '7px', bgcolor: '#F9FAFB', border: '1px dashed #C9BEFB', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                        <Typography sx={{ fontFamily: 'monospace', fontSize: 15, fontWeight: 700, letterSpacing: 1, wordBreak: 'break-all', userSelect: 'all' }}>{recoveryKey}</Typography>
                        <Tooltip arrow title="Copy"><IconButton size="small" onClick={copyKey}><ContentCopyIcon sx={{ fontSize: 17 }} /></IconButton></Tooltip>
                    </Box>
                    <FormControlLabel
                        sx={{ m: 0 }}
                        control={<Checkbox size="small" checked={recoverySaved} onChange={(e) => setRecoverySaved(e.target.checked)} sx={{ p: 0.6, mr: 0.4 }} />}
                        label={<Typography sx={{ fontSize: 12.5 }}>I have stored this key somewhere safe</Typography>}
                    />
                </DialogContent>
                <DialogActions sx={{ px: 2.5, py: 1.8, mt: 0.5, borderTop: '1px solid #E5E7EB' }}>
                    <Button onClick={closeRecovery} disabled={!recoverySaved} sx={{ ...solidBtn, px: 2.5, height: 38, '&.Mui-disabled': { bgcolor: '#E5E7EB', color: '#9CA3AF' } }}>Done</Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
